import axiosInstance from "../../utils/axiosInstance";
// loading
import {
  setBlogs,
  setBlog,
  setBlogsLoading,
  setBlogLoading,
  setBlogsError,
  setBlogError,
  setComments,
  setCommentsError,
  setCommentsLoading,
  setCommentSubmitting,
} from "../../store/slices/blog";
import { AppDispatch } from "../../store/store";
import { isAxiosError } from "axios";
import {
  IComment,
  ICommentRequest,
  ICommentResponseData,
} from "../../store/@types/blogs";
import { IApiResponse } from "../../store/@types/api-response";

const blogEndpoint = "/blog/api/v1/posts";

export const get_blogs = async (
  dispatch: AppDispatch,
  limit: number,
  offset: number,
) => {
  dispatch(setBlogsLoading(true));
  dispatch(setBlogsError(null));
  try {
    const response = await axiosInstance.get(`${blogEndpoint}`, {
      params: { limit, offset },
    });
    dispatch(setBlogs(response.data));
  } catch (err) {
    const message = getErrorMessage(err);
    console.error(
      "Failed to fetch blogs",
      isAxiosError(err) ? (err.response?.data ?? err.message) : err,
    );
    dispatch(setBlogsError(message));
  } finally {
    dispatch(setBlogsLoading(false));
  }
};

export const get_blog = async (dispatch: AppDispatch, id: string) => {
  dispatch(setBlogLoading(true));
  dispatch(setBlog(null));
  dispatch(setBlogError(null));
  try {
    const response = await axiosInstance.get(`${blogEndpoint}/${id}`);
    dispatch(setBlog(response.data.data));
  } catch (err) {
    const message = getErrorMessage(err);
    console.error(
      "Failed to fetch blog",
      isAxiosError(err) ? (err.response?.data ?? err.message) : err,
    );
    dispatch(setBlogError(message));
  } finally {
    dispatch(setBlogLoading(false));
  }
};


export const get_comments = async (
  dispatch: AppDispatch,
  postId: number,
  limit = 10,
  offset = 0,
) => {
  dispatch(setCommentsLoading(true));
  dispatch(setCommentsError(null));
  try {
    const response = await axiosInstance.get<
      IApiResponse<ICommentResponseData>
    >(`${blogEndpoint}/${postId}/comments`, { params: { limit, offset } });
    dispatch(setComments(response.data));
    return true;
  } catch (err) {
    const message = getErrorMessage(err);
    console.error("Failed to fetch blog comments", err);
    dispatch(setCommentsError(message));
    return false;
  } finally {
    dispatch(setCommentsLoading(false));
  }
};

export const create_comment = async (
  dispatch: AppDispatch,
  postId: number,
  data: ICommentRequest,
): Promise<boolean> => {
  dispatch(setCommentSubmitting(true));
  dispatch(setCommentsError(null));
  try {
    await axiosInstance.post<IApiResponse<IComment>>(
      `${blogEndpoint}/${postId}/comments`,
      data,
    );
    return true;
  } catch (err) {
    const message = getErrorMessage(err);
    console.error("Failed to create blog comment", err);
    dispatch(setCommentsError(message));
    return false;
  } finally {
    dispatch(setCommentSubmitting(false));
  }
};

export const update_comment = async (
  dispatch: AppDispatch,
  postId: number,
  commentId: number,
  data: ICommentRequest,
  limit = 10,
  offset = 0,
) => {
  try {
    await axiosInstance.put<IApiResponse<IComment>>(
      `${blogEndpoint}/${postId}/comments/${commentId}`,
      data,
    );
    await get_comments(dispatch, postId, limit, offset);
  } catch (err) {
    const message = getErrorMessage(err);
    console.error("Failed to update blog comment", err);
    dispatch(setCommentsError(message));
    throw new Error(message);
  }
};

export const delete_comment = async (
  dispatch: AppDispatch,
  postId: number,
  commentId: number,
  limit = 10,
  offset = 0,
) => {
  try {
    await axiosInstance.delete(
      `${blogEndpoint}/${postId}/comments/${commentId}`,
    );
    await get_comments(dispatch, postId, limit, offset);
  } catch (err) {
    const message = getErrorMessage(err);
    console.error("Failed to delete blog comment", err);
    dispatch(setCommentsError(message));
    throw new Error(message);
  }
};

const getErrorMessage = (error: unknown) => {
  if (isAxiosError(error)) {
    const responseMessage = error.response?.data?.message;
    return typeof responseMessage === "string"
      ? responseMessage
      : error.message;
  }

  return error instanceof Error
    ? error.message
    : "An unexpected error occurred.";
};
